import * as _ from 'lodash-es';
import React from 'react';
import { useMutation } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import Card, { Cards } from '../../components/Card';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import Form, { Field } from '../../components/Form';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import type { UserSettingsNotificationsInput } from '../../graphql/generated/graphql';
import Link from '../../utils/router/Link';
import * as styles from './UserNotificationSettingsTable.module.scss';

const NOTIFICATION_SCOPES: Record<
  keyof UserSettingsNotificationsInput,
  string[]
> = {
  direct: [
    'Mentions me in a post or comment',
    'Replies to my comment',
    'Edits, archives, comments on, or reacts to a post I own',
    'Post verification requests, reminders, and expirations',
  ],
  participant: [
    'Edits a post I contributed to',
    'Replies to a comment I also commented on',
  ],
  team: [
    'Edits a post in a topic I am a member of',
    'Publishes a post in a topic I am a member of',
  ],
};

const NOTIFICATION_SCOPES_KEYS = Object.keys(NOTIFICATION_SCOPES) as Array<
  keyof UserSettingsNotificationsInput
>;

gql(`
  fragment UserNotificationSettingsTable_settings on UserSettings {
    notifications {
      direct {
        email
        slack
      }
      participant {
        email
        slack
      }
      team {
        email
        slack
      }
    }
  }
`);

const USER_FRAGMENT = gql(`
  fragment UserNotificationSettingsTable_user on User {
    id
    settings {
      ...UserNotificationSettingsTable_settings
    }
  }
`);

const UPDATE_USER_SETTINGS_NOTIFICATIONS_MUTATION = gql(/** @complexity 10 */ `
  mutation UpdateUserSettingsNotifications($notifications: UserSettingsNotificationsInput) {
    updateUserSettings(notifications: $notifications) {
      ...UserNotificationSettingsTable_settings
    }
  }
`);

interface UserNotificationSettingsTableProps {
  slackConnectUrl: string | null;
  slackIntegrated: boolean;
  user: DocumentType<typeof USER_FRAGMENT>;
}

const UserNotificationSettingsTable = ({
  slackConnectUrl,
  slackIntegrated,
  user,
}: UserNotificationSettingsTableProps) => {
  const [updateUserSettings] = useMutation(
    UPDATE_USER_SETTINGS_NOTIFICATIONS_MUTATION,
  );
  const notificationSettings = user.settings?.notifications ?? {};
  const isVerified = useAppSelector(
    (state) => state.session.organization.isVerified,
  );
  const initialValues = NOTIFICATION_SCOPES_KEYS.reduce<
    Record<string, boolean | null | undefined>
  >(
    (memo, scope) => ({
      ...memo,
      [`${scope}-email`]: notificationSettings[scope]?.email,
      [`${scope}-slack`]: notificationSettings[scope]?.slack,
    }),
    { disabled: false },
  );

  const handleChange = (
    scope: keyof UserSettingsNotificationsInput,
    platform: 'slack' | 'email',
    checked: boolean,
  ) => {
    updateUserSettings({
      variables: {
        notifications: {
          [scope]: { [platform]: checked },
        },
      },
      update: (cache, { data }) => {
        // The user settings coming back from the mutation are not normalized, as they don't have an id
        // therefore, we need to manually update the settings of the user in the cache
        if (data?.updateUserSettings) {
          cache.writeFragment({
            fragment: USER_FRAGMENT,
            fragmentName: 'UserNotificationSettingsTable_user',
            id: cache.identify(user),
            data: {
              ...user,
              settings: {
                ...user.settings,
                ...data.updateUserSettings,
              },
            },
          });
        }
      },
    });
  };

  return (
    <Form initialValues={initialValues}>
      {slackIntegrated && (
        <ul className={styles.platformHeadings}>
          <li>Email</li>
          <li>
            <ExternalBrandLogo brand="slack" height={16} />
            <span>Slack</span>
          </li>
        </ul>
      )}

      <Cards>
        {NOTIFICATION_SCOPES_KEYS.map(
          (scope: keyof typeof NOTIFICATION_SCOPES) => {
            const slackToggle = (
              <Field
                disabled={!!slackConnectUrl}
                name={`${scope}-slack`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(scope, 'slack', e.target.checked)
                }
                toggle
                type="checkbox"
              />
            );
            return (
              <Card key={scope}>
                <div className={styles.scopeContainer}>
                  <h4>{_.capitalize(scope)}</h4>
                  <div className={styles.items}>
                    When someone:
                    <ul>
                      {NOTIFICATION_SCOPES[scope].map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className={styles.toggles}>
                    <div>
                      <Tooltip
                        trigger="clickOrHover"
                        placement={slackIntegrated ? 'top' : 'top-end'}
                        content={
                          isVerified
                            ? undefined
                            : 'Notifications are disabled\nuntil your email\nhas been verified'
                        }
                        display="block"
                      >
                        <Field
                          disabled={!isVerified}
                          name={`${scope}-email`}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(scope, 'email', e.target.checked)
                          }
                          toggle
                          type="checkbox"
                        />
                      </Tooltip>
                    </div>
                    {isVerified &&
                      slackIntegrated &&
                      (slackConnectUrl ? (
                        <Link
                          to={slackConnectUrl}
                          className={styles.toggleContainer}
                        >
                          {slackToggle}
                        </Link>
                      ) : (
                        <div className={styles.toggleContainer}>
                          {slackToggle}
                        </div>
                      ))}
                  </div>
                </div>
              </Card>
            );
          },
        )}
      </Cards>
    </Form>
  );
};

export default UserNotificationSettingsTable;
